/**
 * cdt-wishlist — native wishlist engine for cudetoate.ro (replaces the
 * Wishlist Hero app). Port of Vivendo's vv-wishlist.js.
 *
 * - localStorage is the source of truth for display ('cdt_wl'); every
 *   add/remove is mirrored to the TenQ backend (fire-and-forget beacon)
 *   for analytics + email capture.
 * - Anonymous visitor id in 'cdt_wid' (UUID). Logged-in customers get their
 *   email linked to the visitor (transactional only). No customer-id merge —
 *   unverifiable without an App Proxy.
 * - Hearts: any element with [data-vv-wl-toggle] + data-pid/handle/title.
 *   Delegated click handler; state class 'is-saved'.
 * - Header heart + count bubble are Liquid-rendered (our replacement of
 *   snippets/wishlisthero-header-icon.liquid) — JS only keeps counts fresh.
 * - PDP: Wishlist Hero's app embed may still inject its own button into
 *   #wishlist-hero-product-page-button; a small guard removes app-injected
 *   containers so only our Liquid button remains.
 */
(function () {
  'use strict';
  var API = 'https://api.tenq.ro/api/wishlist';
  var SHOP = 'cudetoate';
  var LS_KEY = 'cdt_wl';
  var WID_KEY = 'cdt_wid';

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function wid() {
    var v = null;
    try { v = localStorage.getItem(WID_KEY); } catch (e) { /* noop */ }
    if (!v) {
      v = uuid();
      try { localStorage.setItem(WID_KEY, v); } catch (e) { /* noop */ }
    }
    return v;
  }

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, 200))); } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('vv:wishlist-change', { detail: { count: list.length } }));
  }

  function customerEmail() {
    return (document.body && document.body.dataset.cdtCustomerEmail) || null;
  }

  function beacon(path, payload) {
    try {
      fetch(API + path, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function () { /* noop */ });
    } catch (e) { /* noop */ }
  }

  var api = {
    list: read,
    count: function () { return read().length; },
    has: function (pid) {
      pid = parseInt(pid, 10);
      return read().some(function (i) { return i.p === pid; });
    },
    add: function (item) {
      var pid = parseInt(item.p, 10);
      if (!pid) return;
      var list = read();
      if (!list.some(function (i) { return i.p === pid; })) {
        list.unshift({ p: pid, v: parseInt(item.v, 10) || null, h: item.h || null, t: item.t || null, pr: item.pr || null });
        write(list);
      }
      beacon('/event', {
        shop: SHOP, visitorId: wid(), action: 'add',
        productId: pid, variantId: item.v || null, handle: item.h || null,
        title: item.t || null, price: item.pr || null
      });
    },
    remove: function (pid) {
      pid = parseInt(pid, 10);
      write(read().filter(function (i) { return i.p !== pid; }));
      beacon('/event', { shop: SHOP, visitorId: wid(), action: 'remove', productId: pid });
    },
    toggle: function (item) {
      if (api.has(item.p)) { api.remove(item.p); return false; }
      api.add(item); return true;
    },
    identify: function (email, consent) {
      return fetch(API + '/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: SHOP, visitorId: wid(), email: email, consent: !!consent })
      }).then(function (r) { return r.json(); });
    },
    visitorId: wid
  };
  window.cdtWishlist = api;

  /* ── Heart state sync ─────────────────────────────────────────────── */
  function syncHearts(root) {
    var list = read();
    var ids = {};
    list.forEach(function (i) { ids[i.p] = true; });
    (root || document).querySelectorAll('[data-vv-wl-toggle]').forEach(function (btn) {
      var saved = !!ids[parseInt(btn.dataset.pid, 10)];
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vv-wl-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var saved = api.toggle({
      p: btn.dataset.pid,
      v: btn.dataset.vid,
      h: btn.dataset.handle,
      t: btn.dataset.title,
      pr: btn.dataset.price
    });
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    if (saved) {
      btn.classList.add('cdt-wl-pop');
      setTimeout(function () { btn.classList.remove('cdt-wl-pop'); }, 400);
      bfPriceAlertToast();
    }
  });

  /* ── Black Friday price-alert toast (spec 2026-10-06, board "Favorite") ──
     Teaser phase only, with the price_alert toggle on, after a SAVE (never a
     removal). Known e-mail (logged-in customer, an earlier toast, or the wheel)
     → the alert is registered silently once and the toast auto-hides after 8 s.
     Unknown → the toast asks for the address ("Anunță-mă") and stays open. */
  var WL_EMAIL_KEY = 'cdt_wl_email'; // owned by sections/cdt-wishlist.liquid (JSON {email, consent}) — READ only here
  var BF_EMAIL_KEY = 'cdt_bf_alert_email'; // the toast's own key (plain address); never touches the wishlist page's state
  var BF_ALERT_KEY = 'cdt_bf_alert_2026';
  var BF_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function bfKnownEmail() {
    var e = customerEmail();
    if (!e) { try { e = localStorage.getItem(BF_EMAIL_KEY) || null; } catch (x) { /* noop */ } }
    if (!e) { try { var st = JSON.parse(localStorage.getItem(WL_EMAIL_KEY) || 'null'); e = st && st.email; } catch (x) { /* noop */ } }
    if (!e && window.CdtBfWheel && window.CdtBfWheel.knownEmail) e = window.CdtBfWheel.knownEmail();
    return e || null;
  }
  /* B3: wishlist page banner (teaser) — show the address the BF price e-mail will go to, when we know it. */
  function bfFillWishlistBanner() {
    var slot = document.querySelector('[data-bf-wl-email]');
    if (!slot) return;
    var email = bfKnownEmail();
    if (email) slot.textContent = email;
  }
  function bfAlertOnce(email) {
    var done = null;
    try { done = localStorage.getItem(BF_ALERT_KEY); } catch (x) { /* noop */ }
    if (done === email) return;
    window.CdtBfWheel.priceAlert(email, wid()).then(function (ok) {
      if (ok) { try { localStorage.setItem(BF_ALERT_KEY, email); } catch (x) { /* noop */ } }
    });
  }
  function bfPriceAlertToast() {
    var bf = window.CDT_BF;
    if (!bf || bf.phase !== 'teaser' || !bf.toggles || !bf.toggles.price_alert) return;
    if (!window.CdtBfWheel || !window.CdtBfWheel.priceAlert) return;
    var email = bfKnownEmail();
    var old = document.getElementById('cdt-bf-toast');
    if (old) old.remove();
    var t = document.createElement('div');
    t.id = 'cdt-bf-toast'; t.className = 'cdt-bf-toast'; t.setAttribute('role', 'status');
    t.innerHTML = '<div class="cdt-bf-toast__row">'
      + '<span class="cdt-bf-toast__heart" aria-hidden="true"><svg viewBox="0 0 24 24" fill="#fff"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg></span>'
      + '<span class="cdt-bf-toast__txt"><strong>Salvat la Favorite</strong><span data-bf-toast-msg>Pe <b>6 noiembrie, la 00:05</b>, îți trimitem prețul de Black Friday pentru el.</span></span>'
      + '<button type="button" class="cdt-bf-toast__x" aria-label="Închide">×</button></div>'
      + (email ? '' : '<form class="cdt-bf-toast__form" novalidate>'
        + '<label class="cdt-bf-visually-hidden" for="cdt-bf-toast-email">E-mail</label>'
        + '<input id="cdt-bf-toast-email" type="email" name="email" inputmode="email" autocomplete="email" placeholder="Unde să-ți trimitem prețul?">'
        + '<button type="submit">Anunță-mă</button></form>'
        + '<p class="cdt-bf-toast__err" role="alert" hidden></p>'
        + '<span class="cdt-bf-toast__note">Fără newsletter, doar alerta de preț.</span>');
    document.body.appendChild(t);
    var timer = null;
    var hide = function () { clearTimeout(timer); t.remove(); };
    t.querySelector('.cdt-bf-toast__x').addEventListener('click', hide);
    if (email) {
      bfAlertOnce(email);
      timer = setTimeout(hide, 8000);
      return;
    }
    var form = t.querySelector('form');
    var err = t.querySelector('.cdt-bf-toast__err');
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var v = form.email.value.trim();
      err.hidden = true;
      if (!BF_EMAIL_RE.test(v)) { err.textContent = 'Adresa nu pare completă. Ex.: nume@gmail.com'; err.hidden = false; form.email.focus(); return; }
      var b = form.querySelector('button'); b.disabled = true;
      window.CdtBfWheel.priceAlert(v, wid()).then(function (ok) {
        b.disabled = false;
        if (!ok) { err.textContent = 'Nu am putut salva acum. Încearcă din nou.'; err.hidden = false; return; }
        try {
          localStorage.setItem(BF_EMAIL_KEY, v);
          localStorage.setItem(BF_ALERT_KEY, v);
        } catch (x) { /* noop */ }
        api.identify(v, false).catch(function () { /* noop */ });
        form.remove(); err.remove();
        var note = t.querySelector('.cdt-bf-toast__note'); if (note) note.remove();
        var msg = t.querySelector('[data-bf-toast-msg]');
        msg.textContent = 'Gata! Pe 6 noiembrie, la 00:05, îți scriem pe ' + v + '.';
        timer = setTimeout(hide, 6000);
      });
    });
  }

  function updateHeaderCount() {
    var n = api.count();
    document.querySelectorAll('.cdt-wl-header-count').forEach(function (b) {
      b.textContent = n;
      b.classList.toggle('is-visible', n > 0);
    });
  }
  document.addEventListener('vv:wishlist-change', updateHeaderCount);

  /* ── Wishlist Hero cleanup guard ──────────────────────────────────
     The Hero app embed may still be enabled and inject its own button
     into #wishlist-hero-product-page-button (possibly wiping our Liquid
     button). Remove app containers and re-add ours if it got wiped. */
  var pdpButtonTemplate = null; // clone of our Liquid-rendered button
  function guardPdpButton() {
    var slot = document.getElementById('wishlist-hero-product-page-button');
    if (!slot) return;
    slot.querySelectorAll('[class*="wishlisthero"]').forEach(function (el) { el.remove(); });
    if (!slot.querySelector('[data-vv-wl-toggle]') && pdpButtonTemplate) {
      slot.appendChild(pdpButtonTemplate.cloneNode(true));
      syncHearts(slot);
    }
  }

  /* ── Logged-in email link (transactional only) ────────────────────── */
  function mergeServerList() {
    var email = customerEmail();
    if (email) {
      try {
        var k = 'cdt_wl_cid_linked';
        if (sessionStorage.getItem(k) !== email) {
          beacon('/identify', { shop: SHOP, visitorId: wid(), email: email, consent: false });
          sessionStorage.setItem(k, email);
        }
      } catch (e) { /* noop */ }
    }
  }

  /* PDP hearts (buy cluster, sticky bar, back-in-stock box) carry the
     variant the page opened on. Follow the theme's `variant-change` event so a
     save records the variant the shopper is looking at. data-price is in RON
     (lei), never bani: the Liquid side renders `price | divided_by: 100.0`
     and the event's variant JSON is in bani, hence the /100 here. */
  function wireVariantSync() {
    var vs = document.querySelector('[data-variant-selection]');
    if (!vs) return;
    vs.addEventListener('variant-change', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v || !v.id) return;
      document.querySelectorAll('.cdt-wl-pdp-btn, .cdt-satc__wl, .cdt-bis__wl').forEach(function (btn) {
        btn.dataset.vid = String(v.id);
        if (typeof v.price === 'number') btn.dataset.price = String(v.price / 100);
      });
      var slot = document.getElementById('wishlist-hero-product-page-button');
      var ourBtn = slot && slot.querySelector('[data-vv-wl-toggle]');
      if (ourBtn) pdpButtonTemplate = ourBtn.cloneNode(true);
    });
  }

  function init() {
    var slot = document.getElementById('wishlist-hero-product-page-button');
    var ourBtn = slot && slot.querySelector('[data-vv-wl-toggle]');
    if (ourBtn) pdpButtonTemplate = ourBtn.cloneNode(true);
    wireVariantSync();
    syncHearts();
    updateHeaderCount();
    guardPdpButton();
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      guardPdpButton();
      updateHeaderCount();
      if (tries > 10) clearInterval(t);
    }, 1000);
    mergeServerList();
    bfFillWishlistBanner();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // re-sync hearts on dynamically injected content (quick shop, sliders)
  document.addEventListener('vv:cards-injected', function (e) { syncHearts(e.target); });
  window.cdtWishlistSyncHearts = syncHearts;
})();
